import { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogOut, MapPin, Play, CheckCircle2, XCircle, Radio, Map } from "lucide-react";
import RoutePreviewMap from "@/components/RoutePreviewMap";
import DriverWalletPanel from "@/components/DriverWalletPanel";
import DriverLeaderboardPanel from "@/components/DriverLeaderboardPanel";

const driverApi = axios.create({ baseURL: API });
driverApi.interceptors.request.use((c) => {
  const t = localStorage.getItem("apex_driver_token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

export default function DriverDashboard() {
  const nav = useNavigate();
  const driverRaw = localStorage.getItem("apex_driver");
  const driver = driverRaw ? JSON.parse(driverRaw) : null;
  const [bookings, setBookings] = useState([]);
  const [broadcasting, setBroadcasting] = useState(false);
  const [routeFor, setRouteFor] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await driverApi.get("/driver/bookings"); setBookings(data); }
    catch { toast.error("Load failed"); }
  }, []);
  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, [load]);

  useEffect(() => {
    if (!broadcasting) return;
    if (!navigator.geolocation) { toast.error("Location not supported"); setBroadcasting(false); return; }
    const wid = navigator.geolocation.watchPosition(
      async (pos) => {
        try { await driverApi.post("/driver/location", { lat: pos.coords.latitude, lng: pos.coords.longitude }); }
        catch {}
      },
      () => toast.error("Location permission denied"),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    return () => navigator.geolocation.clearWatch(wid);
  }, [broadcasting]);

  if (!driver) return <Navigate to="/driver/login" replace />;

  const logout = () => {
    localStorage.removeItem("apex_driver_token");
    localStorage.removeItem("apex_driver");
    nav("/driver/login");
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await driverApi.patch(`/driver/bookings/${id}/status`, { status });
      toast.success("Updated");
      // Streak-bonus celebration — fires when the just-completed ride is the
      // driver's 3rd or later ride inside a rolling 60-minute window (server
      // rebated 20% of commission back to them).
      if (data?.streak_bonus) {
        const sb = data.streak_bonus;
        const rebate = Number(sb.commission_rebate || 0).toFixed(2);
        toast.success(
          `On a ${sb.streak_count}-ride streak! +NZ$${rebate} commission rebate`,
          {
            duration: 6000,
            description: "Keep going — every ride inside the next hour keeps the streak alive.",
            className: "!bg-gradient-to-br !from-amber-500 !to-red-500 !text-white !border-amber-400",
          }
        );
      }
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-[#090D14] text-white">
      <header className="border-b border-white/5 bg-[#0b1220]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <div className="text-caption">Driver</div>
            <div className="font-display font-bold text-lg">{driver.name}</div>
            <div className="text-xs text-slate-500 font-mono">{driver.vehicle}</div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="driver-broadcast-toggle" onClick={() => setBroadcasting((b) => !b)} className={`rounded-full ${broadcasting ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 hover:bg-emerald-500/30" : "bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10"}`}>
              <Radio className={`w-4 h-4 mr-2 ${broadcasting ? "animate-pulse" : ""}`} /> {broadcasting ? "Live" : "Go Live"}
            </Button>
            <Button data-testid="driver-logout" onClick={logout} variant="outline" size="icon" className="bg-transparent border-white/15 hover:bg-white/5 text-white"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6 grid md:grid-cols-2 gap-4">
          <DriverWalletPanel apiClient={driverApi} />
          <DriverLeaderboardPanel apiClient={driverApi} />
        </div>
        <h2 className="text-caption mb-3">Active jobs</h2>
        <h1 className="font-display text-3xl font-extrabold">Your queue</h1>
        <p className="text-slate-400 mt-2 text-sm">{bookings.length} ride{bookings.length !== 1 ? "s" : ""} assigned to you.</p>

        <div className="mt-6 space-y-3">
          {bookings.length === 0 && <div className="glass-card rounded-2xl p-8 text-center text-slate-500 gold-border">No active rides. Go live and wait for dispatch.</div>}
          {bookings.map((b) => (
            <div key={b.id} className="glass-card rounded-2xl p-5 gold-border" data-testid={`driver-booking-${b.reference}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-amber-400 font-bold">#{b.reference}</div>
                  <div className="font-display font-semibold text-lg mt-1">{b.rider_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{b.rider_mobile}</div>
                </div>
                <Badge className="bg-blue-400/20 text-blue-300 border-blue-400/40 border font-mono uppercase text-[10px] tracking-wider">{b.status.replace("_", " ")}</Badge>
              </div>
              <div className="mt-4 pt-4 border-t border-white/5 space-y-2 text-sm">
                <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span className="text-slate-300">{b.pickup_location}</span></div>
                <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" /><span className="text-slate-300">{b.dropoff_location}</span></div>
                {b.notes && <div className="text-xs text-slate-500 pt-2 italic">Note: {b.notes}</div>}
                {b.custom_field_answers && Object.keys(b.custom_field_answers).length > 0 && (
                  <div className="pt-2 grid grid-cols-2 gap-1 text-xs font-mono" data-testid={`driver-custom-${b.reference}`}>
                    {Object.entries(b.custom_field_answers).map(([k, v]) => (
                      <div key={k} className="bg-amber-400/5 border border-amber-400/20 rounded px-2 py-1">
                        <span className="text-slate-500 uppercase text-[9px] tracking-wider">{k.replace(/_/g, " ")}</span>
                        <div className="text-amber-400 truncate">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</div>
                      </div>
                    ))}
                  </div>
                )}
                {b.pickup_lat && b.dropoff_lat && (
                  <div className="pt-2">
                    <Button size="sm" variant="outline" onClick={() => setRouteFor(routeFor === b.id ? null : b.id)} className="bg-transparent border-amber-400/40 text-amber-400 hover:bg-amber-400/10" data-testid={`driver-route-${b.reference}`}>
                      <Map className="w-3.5 h-3.5 mr-2" /> {routeFor === b.id ? "Hide route" : "Show route"}
                    </Button>
                    {routeFor === b.id && (
                      <div className="mt-3">
                        <RoutePreviewMap
                          pickup={{ lat: b.pickup_lat, lng: b.pickup_lng }}
                          dropoff={{ lat: b.dropoff_lat, lng: b.dropoff_lng }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {b.status === "assigned" && <Button onClick={() => setStatus(b.id, "in_progress")} className="btn-gold rounded-full flex-1" data-testid={`driver-start-${b.reference}`}><Play className="w-4 h-4 mr-2" /> Start ride</Button>}
                {b.status === "in_progress" && <Button onClick={() => setStatus(b.id, "completed")} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full flex-1" data-testid={`driver-complete-${b.reference}`}><CheckCircle2 className="w-4 h-4 mr-2" /> Complete</Button>}
                <Button onClick={() => setStatus(b.id, "cancelled")} variant="outline" className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full" data-testid={`driver-cancel-${b.reference}`}><XCircle className="w-4 h-4 mr-2" /> Cancel</Button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
